import * as React from 'react';

import isEmpty from 'lodash-es/isEmpty';
import { XIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { preventClickThrough } from '@/util/util';

type ChipSize = 'xs' | 'sm';

interface ChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  size?: ChipSize;
  onDelete?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  deleteIcon?: React.ReactElement;
}

const Chip = ({
  className,
  size = 'sm',
  onDelete,
  deleteIcon = <XIcon />,
  children,
  ref,
  ...props
}: ChipProps & { ref?: React.Ref<HTMLSpanElement> }) => {
  return (
    <Badge
      ref={ref}
      type="secondary"
      flavour="neutral"
      rounded
      size={size}
      className={className}
      trailingIcon={
        onDelete ? (
          <Button
            type="button"
            variant="ghost-neutral"
            size="icon-xss"
            className="ml-1 shrink-0 rounded-full"
            aria-label="Remove"
            onClick={onDelete}
          >
            {deleteIcon}
          </Button>
        ) : undefined
      }
      {...props}
    >
      {children}
    </Badge>
  );
};

interface ChipsProps extends React.HTMLAttributes<HTMLDivElement> {
  chips: string[];
  onDelete?: (chip: string) => void;
  icon?: React.ReactElement;
  size?: ChipSize;
  hideDeleteIcon?: boolean;
  maxHeight?: number;
  chipProps?: Omit<ChipProps, 'size' | 'onDelete'>;
}

const Chips = ({
  className,
  chips = [],
  onDelete,
  icon = <XIcon />,
  size = 'sm',
  hideDeleteIcon = false,
  maxHeight = 200,
  ref,
  chipProps = {},
  ...props
}: ChipsProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const handleDelete = (chip: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
    preventClickThrough(event);
    onDelete?.(chip);
  };

  return (
    <div
      ref={ref}
      className={cn('flex flex-wrap gap-2 overflow-y-auto', className)}
      style={{ maxHeight }}
      {...props}
    >
      {!isEmpty(chips) &&
        chips.map((chip, index) => (
          <Chip
            key={chip}
            size={size}
            onDelete={!hideDeleteIcon && onDelete ? handleDelete(chip) : undefined}
            deleteIcon={<span data-testid={`delete-chip-${index}`}>{icon}</span>}
            {...chipProps}
          >
            {chip}
          </Chip>
        ))}
    </div>
  );
};

export { Chip, Chips };
export type { ChipProps, ChipsProps, ChipSize };
